import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type SpotQuoteParts = {
  paySymbol: string
  rateAmount: string
  rateUnitSymbol: string
}

/** Discrete label for a value readout (e.g. "Cotización", "Precio de mercado"). */
export function AmountFieldLabel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <span className={cn('amount-field-label shrink-0', className)}>{children}</span>
}

/** Label + value row with baseline alignment so labels stay visually secondary. */
export function QuoteFieldRow({
  label,
  children,
  className,
}: {
  label: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5', className)}>
      <AmountFieldLabel>{label}</AmountFieldLabel>
      {children}
    </div>
  )
}

/** Single rate readout: numeric amount + token unit (e.g. header spot price). */
export function SpotRateDisplay({
  amount,
  unitSymbol,
  size = 'lg',
  className,
}: {
  amount: string
  unitSymbol: string
  size?: 'md' | 'lg' | 'xl'
  className?: string
}) {
  const amountSize = {
    md: 'text-base font-semibold',
    lg: 'text-xl font-semibold sm:text-2xl',
    xl: 'text-2xl font-semibold sm:text-3xl',
  }[size]
  const unitSize = {
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-base',
  }[size]

  return (
    <span className={cn('font-mono leading-none', className)}>
      <span className={cn('amount-value', amountSize)}>{amount}</span>
      <span className={cn('amount-unit ml-1.5', unitSize)}>{unitSymbol}</span>
    </span>
  )
}

/** Directed pair quote: 1 PAY = X RECEIVE with distinct amount/unit styling. */
export function SpotQuoteDisplay({
  paySymbol,
  rateAmount,
  rateUnitSymbol,
  size = 'md',
  className,
}: SpotQuoteParts & {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}) {
  const sizeClass = {
    sm: 'text-xs',
    md: 'text-sm sm:text-base',
    lg: 'text-lg sm:text-xl',
    xl: 'text-xl font-semibold sm:text-2xl lg:text-3xl',
  }[size]

  return (
    <span className={cn('font-mono leading-none', sizeClass, className)}>
      <span className="amount-value font-semibold">1</span>
      <span className="amount-unit ml-1">{paySymbol}</span>
      <span className="amount-eq mx-1.5">=</span>
      <span className="amount-value font-semibold">{rateAmount}</span>
      <span className="amount-unit ml-1">{rateUnitSymbol}</span>
    </span>
  )
}
